import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useAppSelector } from '@/store/hooks'
import { usePermission } from '@/hooks/usePermission'
import { chatApi } from './api/chatApi'
import { chatRealtime, type RealtimeStatus } from './realtime/chatRealtime'
import { CHAT_PERMISSIONS } from './permissions'
import {
  applyConversationRead,
  applyMessageCreated,
  removeConversation,
  upsertConversation,
} from './utils/conversationCache'
import type { ChatConversation, ChatEvent, ChatMessage } from './types'

/**
 * Messages 的全局状态（挂在 Layout，整站只一份；不放进 TopBar —— TopBar 会随 Novu provider 重挂）：
 *
 * - 实时连接：有 system:chat:query 就一直连着（顶栏未读数要跟着走），登出 / 没权限就断开。
 * - 未读总数：顶栏角标用，GET /unread-count；相关事件到了防抖重取。
 * - 会话列表：Messages 页打开时才取（useChatList），实时事件到了直接改列表，不每条都重拉。
 *   实时事件只在这里处理一次（列表与会话页各处理会让未读加两次，同 mobile ChatHost）。
 * - 实时没配 / 断线时轮询兜底：未读 30 秒、打开着的会话列表 15 秒（标签页在前台才轮询）。
 * - 换人登录时清空（不能闪现上一个人的会话）。
 */

const UNREAD_POLL_MS = 30_000
const LIST_POLL_MS = 15_000
const UNREAD_DEBOUNCE_MS = 300

interface ChatContextValue {
  /** 有 Messages 权限（system:chat:query） */
  enabled: boolean
  me: string | null
  status: RealtimeStatus
  /** 未读条数，不含免打扰会话（顶栏数字角标） */
  unread: number
  /** 免打扰且有未读的会话数（unread 为 0 时顶栏显示红点） */
  mutedUnread: number
  conversations: ChatConversation[]
  listLoaded: boolean
  listError: boolean
  reloadList: () => Promise<void>
  refreshUnread: () => void
  /** 改名 / 加人 / 新建后用接口回包更新列表里的这一个 */
  upsert: (conversation: ChatConversation) => void
  /** 退群 / 被移出：从列表删掉 */
  remove: (conversationId: string) => void
  /**
   * 我发出的消息落库了：并进列表（最新一条、挪到最前）。实时连着时 message.created 也会到，
   * applyMessageCreated 幂等；实时没配 / 断线时只能靠这一步，列表才不会等到下次轮询。
   */
  noteSent: (message: ChatMessage) => void
  /** 我标了已读：列表里这个会话的未读清零、角标重取（同上，不等 conversation.read 事件） */
  noteRead: (conversationId: string, messageId: string) => void
  /** Messages 页挂载期间登记（决定要不要取 / 轮询列表）；返回注销函数 */
  subscribeList: () => () => void
  /** 正在看的会话（标签页在前台且会话开着）：它的新消息不加未读 */
  setViewing: (conversationId: string | null) => void
}

const ChatContext = createContext<ChatContextValue | null>(null)

function useVisible(): boolean {
  const [visible, setVisible] = useState(() => typeof document === 'undefined' || document.visibilityState === 'visible')
  useEffect(() => {
    const onChange = () => setVisible(document.visibilityState === 'visible')
    document.addEventListener('visibilitychange', onChange)
    return () => document.removeEventListener('visibilitychange', onChange)
  }, [])
  return visible
}

export function ChatProvider({ children }: { children: ReactNode }) {
  const me = useAppSelector((s) => s.user.userId)
  const { hasPermission } = usePermission()
  const enabled = !!me && hasPermission(CHAT_PERMISSIONS.query)
  const visible = useVisible()

  const [status, setStatus] = useState<RealtimeStatus>('idle')
  const [unread, setUnread] = useState(0)
  const [mutedUnread, setMutedUnread] = useState(0)
  const [conversations, setConversations] = useState<ChatConversation[]>([])
  const [listLoaded, setListLoaded] = useState(false)
  const [listError, setListError] = useState(false)
  const [listSubscribers, setListSubscribers] = useState(0)

  const viewing = useRef<string | null>(null)
  const meRef = useRef(me)
  meRef.current = me
  /** 换人 / 关掉时 +1：在途请求回来后作废 */
  const epoch = useRef(0)
  const listRequest = useRef(0)
  const unreadRequest = useRef(0)
  const unreadTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const listLoadedRef = useRef(false)
  listLoadedRef.current = listLoaded
  /**
   * 列表的同步副本：实时回调里要马上知道改没改到（setState 的 updater 在 React 18 起不是同步跑的），
   * 所以所有写列表的地方都经 writeList 先算出新列表、再 setState。
   */
  const listRef = useRef<ChatConversation[]>([])
  const writeList = useCallback((fn: (list: ChatConversation[]) => ChatConversation[]) => {
    const next = fn(listRef.current)
    if (next === listRef.current) return
    listRef.current = next
    setConversations(next)
  }, [])

  // 换人 / 失去权限：清空所有状态
  useEffect(() => {
    epoch.current += 1
    listRef.current = []
    setConversations([])
    setListLoaded(false)
    setListError(false)
    setUnread(0)
    setMutedUnread(0)
    viewing.current = null
  }, [me, enabled])

  const fetchUnread = useCallback(async () => {
    const e = epoch.current
    const request = ++unreadRequest.current
    try {
      const res = await chatApi.unreadCount()
      if (e !== epoch.current || request !== unreadRequest.current) return
      setUnread(res?.total ?? 0)
      // 旧接口没有这个字段：当作 0（不显示红点）
      setMutedUnread(res?.mutedUnreadConversations ?? 0)
    } catch {
      // 失败保留原数字，下次事件 / 轮询再取
    }
  }, [])

  const refreshUnread = useCallback(() => {
    if (unreadTimer.current) clearTimeout(unreadTimer.current)
    unreadTimer.current = setTimeout(() => {
      unreadTimer.current = null
      void fetchUnread()
    }, UNREAD_DEBOUNCE_MS)
  }, [fetchUnread])

  useEffect(() => () => {
    if (unreadTimer.current) clearTimeout(unreadTimer.current)
  }, [])

  const reloadList = useCallback(async () => {
    const e = epoch.current
    const request = ++listRequest.current
    try {
      const items = await chatApi.listConversations()
      if (e !== epoch.current || request !== listRequest.current) return
      writeList(() => items)
      setListLoaded(true)
      setListError(false)
    } catch {
      if (e !== epoch.current || request !== listRequest.current) return
      setListError(true)
    }
  }, [writeList])

  // 实时连接 + 首次未读数
  useEffect(() => {
    if (!enabled) return
    void fetchUnread()
    const offStatus = chatRealtime.onStatus(setStatus)
    const release = chatRealtime.acquire()
    return () => {
      release()
      offStatus()
      setStatus('idle')
    }
  }, [enabled, fetchUnread])

  // 实时事件 → 改列表 / 未读
  useEffect(() => {
    if (!enabled) return
    const onEvent = (event: ChatEvent) => {
      const current = meRef.current
      switch (event.type) {
        case 'conversation.created':
        case 'conversation.updated':
          if (listLoadedRef.current) void reloadList()
          if (event.type === 'conversation.created') refreshUnread()
          return
        case 'conversation.removed':
          writeList((list) => removeConversation(list, event.conversationId))
          refreshUnread()
          return
        case 'message.created': {
          const msg = event.message
          const result = applyMessageCreated(
            listRef.current, event.conversationId, msg, current, viewing.current === event.conversationId,
          )
          writeList(() => result.list)
          if (!result.found && listLoadedRef.current) void reloadList()
          if (msg.senderId !== current && msg.kind !== 'system' && (result.applied || !result.found)) refreshUnread()
          return
        }
        case 'conversation.read':
          writeList((list) =>
            applyConversationRead(list, event.conversationId, event.userId, event.lastReadMessageId, current),
          )
          if (event.userId === current) refreshUnread()
          return
      }
    }
    const onResync = () => {
      refreshUnread()
      if (listLoadedRef.current) void reloadList()
    }
    const offEvent = chatRealtime.onEvent(onEvent)
    const offResync = chatRealtime.onResync(onResync)
    return () => {
      offEvent()
      offResync()
    }
  }, [enabled, refreshUnread, reloadList, writeList])

  // 列表：Messages 页打开时取；回到前台补拉
  const listWanted = enabled && listSubscribers > 0
  useEffect(() => {
    if (listWanted && visible) void reloadList()
  }, [listWanted, visible, reloadList])

  // 实时没连上时轮询兜底（标签页在前台才轮询）
  const polling = enabled && visible && status !== 'connected'
  useEffect(() => {
    if (!polling) return
    const timer = setInterval(() => void fetchUnread(), UNREAD_POLL_MS)
    return () => clearInterval(timer)
  }, [polling, fetchUnread])
  useEffect(() => {
    if (!polling || !listWanted) return
    const timer = setInterval(() => void reloadList(), LIST_POLL_MS)
    return () => clearInterval(timer)
  }, [polling, listWanted, reloadList])
  // 回到前台：未读补一次
  useEffect(() => {
    if (enabled && visible) refreshUnread()
  }, [enabled, visible, refreshUnread])

  const upsert = useCallback((conversation: ChatConversation) => {
    writeList((list) => upsertConversation(list, conversation))
  }, [writeList])

  const remove = useCallback(
    (conversationId: string) => {
      writeList((list) => removeConversation(list, conversationId))
      refreshUnread()
    },
    [refreshUnread, writeList],
  )

  const noteSent = useCallback(
    (message: ChatMessage) => {
      const result = applyMessageCreated(listRef.current, message.conversationId, message, meRef.current, true)
      writeList(() => result.list)
      if (!result.found && listLoadedRef.current) void reloadList()
    },
    [reloadList, writeList],
  )

  const noteRead = useCallback(
    (conversationId: string, messageId: string) => {
      const current = meRef.current
      if (current) writeList((list) => applyConversationRead(list, conversationId, current, messageId, current))
      refreshUnread()
    },
    [refreshUnread, writeList],
  )

  const subscribeList = useCallback(() => {
    setListSubscribers((n) => n + 1)
    return () => setListSubscribers((n) => n - 1)
  }, [])

  const setViewing = useCallback((conversationId: string | null) => {
    viewing.current = conversationId
  }, [])

  const value = useMemo<ChatContextValue>(
    () => ({
      enabled,
      me,
      status,
      unread,
      mutedUnread,
      conversations,
      listLoaded,
      listError,
      reloadList,
      refreshUnread,
      upsert,
      remove,
      noteSent,
      noteRead,
      subscribeList,
      setViewing,
    }),
    [enabled, me, status, unread, mutedUnread, conversations, listLoaded, listError, reloadList, refreshUnread, upsert, remove, noteSent, noteRead, subscribeList, setViewing],
  )

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}

export function useChat(): ChatContextValue {
  const ctx = useContext(ChatContext)
  if (!ctx) throw new Error('useChat must be used inside <ChatProvider>')
  return ctx
}

/** Messages 页用：登记要列表，返回列表状态。 */
export function useChatList() {
  const chat = useChat()
  const { subscribeList } = chat
  useEffect(() => subscribeList(), [subscribeList])
  return chat
}
